import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { loadBootstrap } from '../services/bootstrapService';
import { useAuth } from './AuthContext';
import { useMerchant } from './MerchantContext';
import {
  FEATURE_SUBSCRIPTION_ENFORCEMENT,
  useFeatureFlags,
} from './FeatureFlagContext';

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const { currentMerchant, loading: merchantLoading } = useMerchant();
  const { isEnabled, loading: flagsLoading } = useFeatureFlags();
  const enforcementEnabled = isEnabled(FEATURE_SUBSCRIPTION_ENFORCEMENT);

  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const merchantId = currentMerchant?.id ?? null;

  const fetchPlans = async () => {
    try {
      const response = await api.get('/subscriptions/plans/');
      if (response.data.success) {
        setPlans(response.data.plans);
      }
    } catch (err) {
      console.error('Error fetching subscription plans:', err);
    }
  };

  const fetchSubscription = async () => {
    if (!isAuthenticated() || !currentMerchant) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await api.get('/subscriptions/status/');
      if (response.data.success) {
        setSubscription(response.data.subscription);
      }
    } catch (err) {
      console.error('Error fetching subscription:', err);
      setError(err.response?.data?.error || 'Failed to fetch subscription');
    } finally {
      setLoading(false);
    }
  };

  // Upload a GCash renewal receipt. Returns { success, subscription?, error? }.
  const payForSubscription = async (imageFile) => {
    try {
      const formData = new FormData();
      formData.append('receipt_image', imageFile);
      const response = await api.post('/subscriptions/pay/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (response.data.success) {
        setSubscription(response.data.subscription);
        return { success: true, subscription: response.data.subscription };
      }
      return { success: false, error: 'Failed to record payment' };
    } catch (err) {
      console.error('Error paying for subscription:', err);
      const data = err.response?.data;
      const message =
        data?.errors?.receipt_image ||
        data?.error ||
        'We could not verify this receipt. Please try again.';
      return { success: false, error: message };
    }
  };

  // Per-order accrued service-fee breakdown, loaded on demand when the billing
  // page expands the accrued-fees section. Returns { entries, count, total }.
  const getServiceFeeBreakdown = async () => {
    const response = await api.get('/subscriptions/service-fees/');
    if (response.data.success) {
      return {
        entries: response.data.entries,
        count: response.data.count,
        total: response.data.total,
      };
    }
    throw new Error('Failed to load service fee breakdown');
  };

  const isTrialActive = () => subscription?.in_trial === true;

  const isActive = () => {
    if (!enforcementEnabled) return true;
    return subscription?.has_access === true;
  };

  const getTrialDaysRemaining = () => subscription?.days_left || 0;

  const getCurrentPlan = () => subscription?.plan || null;

  const getPlan = () => (plans.length > 0 ? plans[0] : null);

  const getAmountDue = () => subscription?.amount_due || null;

  const getPlatformGcash = () => subscription?.gcash || null;

  const hasTrialExpired = () => {
    if (!enforcementEnabled) return false;
    if (!subscription) return false;
    return subscription.status === 'expired' && subscription.has_access !== true;
  };

  const isSuspended = () => {
    if (!enforcementEnabled) return false;
    return subscription?.status === 'suspended';
  };

  const isCancelled = () => {
    if (!enforcementEnabled) return false;
    return subscription?.status === 'cancelled';
  };

  const needsPaywall = () => {
    if (!enforcementEnabled) return false;
    if (!subscription) return false;
    return subscription.has_access !== true;
  };

  const fetchSubscriptionRef = useRef(fetchSubscription);
  useEffect(() => {
    fetchSubscriptionRef.current = fetchSubscription;
  });

  const refresh = useCallback(async () => {
    await fetchSubscriptionRef.current();
  }, []);

  useEffect(() => {
    fetchPlans();
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (isAuthenticated() && currentMerchant) {
      setLoading(true);
      loadBootstrap()
        .then((data) => {
          if (!cancelled) {
            setSubscription(data.subscription ?? null);
          }
        })
        .catch((err) => {
          if (!cancelled) {
            console.error('Error fetching subscription:', err);
            setError(err.response?.data?.error || 'Failed to fetch subscription');
          }
        })
        .finally(() => {
          if (!cancelled) {
            setLoading(false);
          }
        });
    } else {
      setSubscription(null);
      // While the merchant context is still hydrating we don't yet know the
      // subscription, so stay in a loading state. Resolving to false here would
      // briefly expose subscription=null and make route guards bounce the user
      // to /trial-expired (then onward to /admin) on refresh.
      setLoading(merchantLoading);
    }

    return () => { cancelled = true; };
  }, [merchantId, merchantLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const value = {
    subscription,
    plans,
    // Feature flags gate enforcement, so the subscription state isn't truly
    // "ready" for route guards until the flags have loaded too. Folding it in
    // here keeps every guard from acting on a transient enforcementEnabled.
    loading: loading || flagsLoading,
    error,
    enforcementEnabled,
    payForSubscription,
    isTrialActive,
    isActive,
    getTrialDaysRemaining,
    getCurrentPlan,
    getPlan,
    getAmountDue,
    getPlatformGcash,
    getServiceFeeBreakdown,
    hasTrialExpired,
    isSuspended,
    isCancelled,
    needsPaywall,
    refresh,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
